import { error, warn } from 'cc';
import StreamingPlayerAdapter, { CommonStreamingErrorName } from '../StreamingPlayerAdapter';
import StreamingError from '../../StreamingError';
import type MediaMTXWebRTCReader from './media-mtx-player';
import Delay from '../../../common/scripts/Toolkit/Utils/Delay';
import { joinURL } from '../../../common/scripts/Toolkit/Utils/Utility';

export enum MediaMTXStreamingErrorName {
    STREAM_NOT_FOUND = 'stream not found',
    BAD_STATUS_CODE = 'bad status code',
    PEER_CONNECTION_CLOSED = 'peer connection closed',
    FAILED_TO_FETCH = 'Failed to fetch',
    CLOSED = 'closed',
}

const errorNameList = Object.values(MediaMTXStreamingErrorName);
const EVENT_VIDEO_START_PLAY = 'VIDEO_START_PLAY';
const EVENT_PLAY_ID_CHANGE = 'PLAY_ID_CHANGE';

export default class MediaMTXStreamingPlayerAdapter extends StreamingPlayerAdapter {
    private _webRTCReaders: MediaMTXWebRTCReader[] = [];
    private _playPromise: Promise<void> = null;
    private _isErrorEmitted: boolean = false;
    private _playID: number = 0;

    constructor(public readonly videoElement: HTMLVideoElement) {
        super();
        if (videoElement.requestVideoFrameCallback === undefined) {
            warn('HTMLVideoElement does not support requestVideoFrameCallback, listening to play event instead');
            videoElement.addEventListener('play', () => this.emit(EVENT_VIDEO_START_PLAY));
        }
        videoElement.addEventListener('error', (event) => {
            this.emit(MediaMTXStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.OTHER_ERROR, event.message));
        });
        this.videoElement.addEventListener('resize', this.onResizeCallback);
    }

    public get muted(): boolean {
        return this.videoElement.muted;
    }
    public set muted(value: boolean) {
        this.videoElement.muted = value;
    }

    public get volume(): number {
        return this.videoElement.volume;
    }
    public set volume(value: number) {
        this.videoElement.volume = value;
    }

    public get controls(): boolean {
        return this.videoElement.controls;
    }
    public set controls(value: boolean) {
        this.videoElement.controls = value;
    }

    public get playsinline(): boolean {
        return this.videoElement.playsInline;
    }
    public set playsinline(value: boolean) {
        this.videoElement.playsInline = value;
    }

    public get loop(): boolean {
        return this.videoElement.loop;
    }
    public set loop(value: boolean) {
        this.videoElement.loop = value;
    }

    public get height(): number {
        return this.videoElement.videoHeight;
    }

    public get width(): number {
        return this.videoElement.videoWidth;
    }

    private get playID(): number {
        return this._playID;
    }
    private set playID(value: number) {
        if (value === this._playID) {
            return;
        }
        this._playID = value;
        this.emit(EVENT_PLAY_ID_CHANGE);
    }

    public async play(videoUrl: string, audioUrl?: string): Promise<void> {
        this.playID++;
        // wait for any ongoing play promise to resolve before starting a new one
        if (this._playPromise) {
            const { playID } = this;
            await this._playPromise;
            if (playID !== this.playID) {
                return Promise.resolve();
            }
        }
        this._playPromise = this._play(videoUrl, audioUrl).then(() => {
            this._playPromise = null;
        });
        return this._playPromise;
    }

    public async stop(): Promise<void> {
        const { playID } = this;
        if (!this.videoElement.paused) {
            await this.pause();
        }
        if (playID !== this.playID) {
            // play has been called again, abort this stop
            return;
        }
        this._clear();
        this.videoElement.srcObject = null;
        // load after srcObject is cleared to ensure no view is displayed
        await this.load();
    }

    public async resume(): Promise<void> {
        const { playID } = this;
        await (this._playPromise ?? Promise.resolve());
        if (playID !== this.playID) {
            // play has been called again, abort this resume
            return;
        }
        this._playPromise = this.videoElement.play();
        await this._playPromise;
    }

    public async pause(): Promise<void> {
        const { playID } = this;
        await (this._playPromise ?? Promise.resolve());
        if (playID !== this.playID) {
            // play has been called again, abort this pause
            return;
        }
        this.videoElement.pause();
    }

    public async load(): Promise<void> {
        const { playID } = this;
        await (this._playPromise ?? Promise.resolve());
        if (playID !== this.playID) {
            // play has been called again, abort this load
            return;
        }
        this.videoElement.load();
    }

    private async _play(videoUrl: string, audioUrl?: string): Promise<void> {
        const { playID } = this;

        // prepare media stream
        let mediaStream: MediaStream = null;
        if (this.videoElement.srcObject) {
            mediaStream = this.videoElement.srcObject as MediaStream;
            // clear existing tracks before playing new ones
            this._clear();
        } else {
            mediaStream = new MediaStream();
            this.videoElement.srcObject = mediaStream;
        }

        // create and start MediaMTXWebRTCReaders
        try {
            await Promise.race([
                Promise.all(
                    [videoUrl, audioUrl].map(async (url) => {
                        if (!url) {
                            return;
                        }
                        const urlObject = new URL(url);
                        urlObject.pathname = joinURL(urlObject.pathname, 'whep');
                        await new Promise<void>((resolve, reject) => {
                            const webRTCReader = new window.MediaMTXWebRTCReader({
                                url: urlObject.toString(),
                                onError: (err) => {
                                    if (this.playID !== playID) {
                                        reject(new Error('Play already aborted, no need to handle error'));
                                        return;
                                    }
                                    // ignore error if the reader has been replaced
                                    if (!this._webRTCReaders.includes(webRTCReader)) {
                                        reject(new Error('Reader already replaced, no need to handle error'));
                                        return;
                                    }
                                    if (this._isErrorEmitted) {
                                        reject(new Error('Error already emitted, no need to handle again'));
                                        return;
                                    }
                                    error(`MediaMTXWebRTCReader error: ${err}`);
                                    const trimmedErr = err.match(/^(?:(?:Type)?Error: )?(.+?)(?:, retrying in some seconds)?$/)?.[1];
                                    const errorName =
                                        errorNameList.includes(trimmedErr as MediaMTXStreamingErrorName) ||
                                        trimmedErr.includes(MediaMTXStreamingErrorName.BAD_STATUS_CODE)
                                            ? (trimmedErr as MediaMTXStreamingErrorName)
                                            : CommonStreamingErrorName.UNKNOWN_ERROR;
                                    const streamingError = new StreamingError(errorName, err);
                                    this.emit(MediaMTXStreamingPlayerAdapter.EVENT_TYPE.Error, streamingError);
                                    this._isErrorEmitted = true;
                                    reject(streamingError);
                                },
                                onTrack: (event) => {
                                    // ignore track event if the reader has been replaced
                                    if (!this._webRTCReaders.includes(webRTCReader)) {
                                        return;
                                    }
                                    const [stream] = event.streams;
                                    if (!stream) {
                                        return;
                                    }
                                    // add tracks to the media stream
                                    stream.getTracks().forEach((track) => mediaStream.addTrack(track));
                                    resolve();
                                },
                            });
                            // do not use built-in retry mechanism
                            webRTCReader.allowRetry = false;
                            this._webRTCReaders.push(webRTCReader);
                        });
                    }),
                ),
                Delay.event(EVENT_PLAY_ID_CHANGE, this),
            ]);
        } catch (err) {
            return;
        }
        if (playID !== this.playID) {
            // play has been called again, abort this play
            return;
        }

        // play video after tracks are added
        this.videoElement.requestVideoFrameCallback?.(() => this.emit(EVENT_VIDEO_START_PLAY));
        this.videoElement.play().catch((err) => warn(err));
        await Promise.race([Delay.event(EVENT_VIDEO_START_PLAY, this), Delay.event(EVENT_PLAY_ID_CHANGE, this)]);
        if (playID === this.playID) {
            // only emit play event if playID has not changed (i.e. no new play call)
            this.emit(MediaMTXStreamingPlayerAdapter.EVENT_TYPE.Play);
        }
    }

    private _clear() {
        const mediaStream = this.videoElement.srcObject as MediaStream;
        if (!mediaStream) {
            return;
        }
        mediaStream.getTracks().forEach((track) => {
            track.stop();
            mediaStream.removeTrack(track);
        });
        this._webRTCReaders.forEach((reader) => reader.close());
        this._webRTCReaders.length = 0;
        this._isErrorEmitted = false;
    }
}
